import Head from 'next/head'
import {GetStaticProps, GetStaticPaths, NextPage} from 'next'
import {getArticleData, getAllArticlePaths} from "../../src/data/content";
import {MainLayout} from "../../src/components/main-layout";

import {Container, Paper, Box, Typography, Grid, Avatar} from '@mui/material';
import {MDXRemote, MDXRemoteSerializeResult} from 'next-mdx-remote'
import {Title} from '../../src/components/remark/title'
import {Intro} from "../../src/components/remark/intro"
import {Youtube} from "../../src/components/remark/youtube";
import {OfferLink} from "../../src/components/remark/offer-link";
import {HiddenMarkup} from "../../src/components/remark/hidden-markup";
import {Temp} from "../../src/components/remark/temp";
import {VisaRequirements} from "../../src/components/remark/visa-requirements";
import {Meters} from "../../src/components/remark/meters";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {Forex} from "../../src/components/remark/forex";
import {Img} from "../../src/components/remark/img";
import {Vimeo} from "../../src/components/remark/vimeo";
import {Dailymotion} from "../../src/components/remark/dailymotion";
import googleBucketLoader from "../../src/lib/google-bucket-loader";
import Image from "next/legacy/image";
import React from "react";
import {A} from "../../src/components/remark/a";
import {ArticleDate} from "../../src/components/article/article-date";
import {fileUrlToUrl} from "next-translate-routes";
import {NextSeo} from "next-seo";
import {useRouter} from "next-translate-routes/router";

import articleDatabase from "../../src/generated/article-database.json";
import {TemperaturesChart} from "../../src/components/remark/temperatures-chart";

interface ArticleProps {
    markdownData: {
        frontmatter: Record<string, string>;
        subDir: string;
        source: MDXRemoteSerializeResult;
    }
}

const initialComponents = {
    Title,
    Intro,
    OfferLink,
    Youtube,
    Overview: HiddenMarkup,
    Temp,
    VisaRequirements,
    Meters,
    Forex,
    Vimeo,
    Dailymotion,
    a:A
}


const ArticlePage: NextPage<ArticleProps> = ({markdownData}) => {

    const router = useRouter()

    const {pathname, locales, locale, query} = router

    const {
        title, header_alt, header_blurDataURL, author_blurDataURL,
        authorName, header_image, date, readingTime,
        description
    } = markdownData.frontmatter

    const {subDir, source} = markdownData

    const components = {
        img: (props: any) => {
            // console.log(props)
            return <Img
                {...props}
                src={`/content/articles/${subDir}/${props.src}`}/>
        },
        TemperaturesChart: (props: any) => <TemperaturesChart tripId={props.tripId}/>,
        ...initialComponents
    }


    return (
        <div>
            <Head>
                <title>{title + " | SeaCrush"} </title>
            </Head>

            <NextSeo
                facebook={{
                    appId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID as string
                }}
                openGraph={{
                    url: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}${fileUrlToUrl({
                        pathname: pathname,
                        query: query
                    }, router.locale || "en")}`,
                    type: "article",
                    article: {
                      authors: [ authorName],
                        publishedTime: date,
                    },
                    title: title,
                    description: description,
                    images: [
                        {
                            width: 640,
                            height: 426,
                            type: "image/webp",
                            url: `${process.env.NEXT_PUBLIC_IMAGE_ROOT}/content/articles/${subDir}/img/${header_image.replace(".webp", `_640__.webp`)}`
                        },
                        {
                            width: 1920,
                            height: 1280,
                            type: "image/webp",
                            url: `${process.env.NEXT_PUBLIC_IMAGE_ROOT}/content/articles/${subDir}/img/${header_image.replace(".webp", `_1920__.webp`)}`
                        }
                    ]
                }}
                twitter={{
                    cardType: "summary_large_image"
                }}
                languageAlternates={
                    locales?.filter((l) => l !== locale && l !== "default").map(
                        (l) => {
                            // @ts-ignore
                            return {
                                hrefLang: l,
                                href: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}` + fileUrlToUrl({
                                        pathname,
                                        query: {
                                            // @ts-ignore
                                            articleSlug: articleDatabase[subDir].slugs[l].articleSlug
                                        }
                                    },
                                    l)
                            }
                        }
                    )
                }
            />
            <Container maxWidth="md" sx={{
                px: {
                    xs: 0,
                    sm: 2
                }
            }}>

                <Paper elevation={8} sx={{my: 3}}>
                    <header>

                        <Box sx={{
                            position: "relative", height: {
                                xs: 200,
                                md: 400
                            }, overflow: "hidden"
                        }}>
                            <Image
                                loader={googleBucketLoader}
                                priority
                                alt={header_alt}
                                blurDataURL={header_blurDataURL}
                                placeholder={"blur"}
                                src={`/content/articles/${subDir}/img/${header_image}`}
                                layout={"fill"}
                                objectFit={"cover"}
                            />
                            <Typography variant={"h1"} sx={{
                                fontSize: "1rem",
                                color: "white",
                                backgroundColor: "rgba(0,0,0,.4)",
                                display: "block",
                                zIndex: 1,
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 1
                            }}>{title}</Typography>
                        </Box>
                    </header>
                    <Box sx={{
                        p: {
                            xs: 2,
                            md: 4
                        }
                    }}>
                        <article>
                            <div>

                                <Grid container spacing={2} sx={{pt: 0}} alignItems={"center"}>
                                    <Grid item>
                                        <Avatar sx={{width: 72, height: 72}}
                                        >
                                            <Image
                                                alt={authorName}
                                                priority
                                                blurDataURL={author_blurDataURL}
                                                placeholder={"blur"}
                                                loader={googleBucketLoader}
                                                src={`/content/articles/${subDir}/author.webp`} width={72}
                                                height={72}/>
                                        </Avatar>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant={"subtitle1"}>{authorName}</Typography>
                                        <Typography variant={"subtitle2"} color={"textSecondary"}><ArticleDate
                                            dateString={date}/> • {readingTime} min read</Typography>

                                    </Grid>
                                </Grid>

                            </div>
                            <div>
                                <MDXRemote {...source} components={components}/>
                            </div>
                        </article>
                    </Box>
                </Paper>
            </Container>
        </div>
    )
}


ArticlePage.getLayout = (page) => (
    <MainLayout>
        {page}
    </MainLayout>
);

export default ArticlePage

export const getStaticPaths: GetStaticPaths = async (context) => {
    const locales = context.locales?.filter((val) => val !== 'default') || []
    const paths = getAllArticlePaths(locales)
    return {
        paths,
        fallback: false
    }
}

export const getStaticProps: GetStaticProps = async ({params, locale}) => {
    if (locale === 'default') {
        return {notFound: true};
    }

    const markdownData = await getArticleData(params?.articleSlug as string, locale)
    return {
        props: {
            markdownData,
            ...(await serverSideTranslations(locale as string, ['frontend']))
        }
    };
}
